// Google Cloud Storage 서비스
// Signed URL 생성 및 비디오 URL 관리

class GCSService {
  constructor() {
    this.cache = new Map(); // URL 캐시 (1시간 유효)
  }

  /**
   * GCS 파일명을 받아서 Signed URL 생성
   * @param {string} fileName - GCS 버킷 내 파일 경로 (예: 'videos/spring-q1.mp4')
   * @param {string} action - 'read' 또는 'upload' (기본값: 'read')
   * @returns {Promise<string>} Signed URL
   */
  async getSignedUrl(fileName, action = 'read') {
    if (!fileName) {
      throw new Error('fileName이 필요합니다.');
    }

    // 캐시 확인 (읽기 전용)
    const cacheKey = `${fileName}_${action}`;
    if (action === 'read' && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      // 만료 시간 확인 (50분 후 캐시 무효화)
      if (new Date() < new Date(cached.expiresAt - 10 * 60 * 1000)) {

        return cached.url;
      }
      this.cache.delete(cacheKey);
    }

    try {

      
      const response = await fetch('/api/gcs-signed-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: fileName,
          action: action,
          ...(action === 'upload' && { contentType: this.getContentType(fileName) })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`GCS API 오류: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(`GCS 오류: ${result.error}`);
      }

      const signedUrl = result.data.signedUrl;
      
      // 읽기 URL만 캐시 (업로드 URL은 일회성)
      if (action === 'read') {
        this.cache.set(cacheKey, {
          url: signedUrl,
          expiresAt: result.data.expiresAt
        });
      }



      
      // URL 유효성 검증
      try {
        const urlTest = new URL(signedUrl);
        if (!urlTest.hostname.includes('googleapis.com') && !urlTest.hostname.includes('storage.cloud.google.com')) {
          console.error('GCS: 유효하지 않은 URL 호스트:', urlTest.hostname);
        }

      } catch (urlError) {
        console.error('GCS: URL 파싱 오류:', urlError);
      }
      
      return signedUrl;
      
    } catch (error) {
      console.error('GCS Signed URL 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 비디오 파일들을 위한 Signed URL 배치 생성
   * @param {Array<string>} fileNames - 파일명 배열
   * @returns {Promise<Object>} fileName -> signedUrl 매핑
   */
  async getBatchSignedUrls(fileNames) {
    const results = {};
    const promises = fileNames.map(async (fileName) => {
      try {
        const url = await this.getSignedUrl(fileName);
        results[fileName] = url;
      } catch (error) {
        console.error(`파일 ${fileName} Signed URL 생성 실패:`, error);
        results[fileName] = null;
      }
    });

    await Promise.all(promises);
    return results;
  }

  /**
   * 면접 질문 데이터의 영상 필드를 Signed URL로 변환
   * @param {Array<Object>} questions - 면접 질문 배열
   * @returns {Promise<Array<Object>>} URL이 변환된 질문 배열
   */
  async processInterviewQuestions(questions) {
    if (!Array.isArray(questions)) {
      return questions;
    }

    // 영상이 있는 질문들만 필터링
    const questionsWithVideo = questions.filter(q => q.영상 && q.영상.trim());
    
    if (questionsWithVideo.length === 0) {

      return questions;
    }

    // GCS 파일명들 추출 (gs:// 프리픽스 제거)
    const fileNames = questionsWithVideo.map(q => {
      const videoPath = q.영상.trim();
      // gs://ncsdevapp/ 제거하고 순수 파일 경로만 추출
      if (videoPath.startsWith('gs://')) {
        const withoutProtocol = videoPath.replace(/^gs:\/\/[^\/]+\//, '');
        return withoutProtocol.trim();
      }
      return videoPath;
    });
    
    const uniqueFileNames = [...new Set(fileNames)];



    // 배치로 Signed URL 생성
    const urlMap = await this.getBatchSignedUrls(uniqueFileNames);

    // 질문 데이터에 Signed URL 적용
    const processedQuestions = questions.map(question => {
      if (question.영상 && question.영상.trim()) {
        const originalPath = question.영상.trim();
        let fileName = originalPath;
        
        // gs:// 프리픽스 제거
        if (originalPath.startsWith('gs://')) {
          fileName = originalPath.replace(/^gs:\/\/[^\/]+\//, '').trim();
        }
        
        const signedUrl = urlMap[fileName];
        
        if (signedUrl) {
          return {
            ...question,
            영상: signedUrl,
            _originalFileName: fileName, // 원본 파일명 보존
            _originalPath: originalPath // 원본 전체 경로 보존
          };
        } else {
          console.warn(`파일 ${fileName}의 Signed URL 생성 실패`);
          return {
            ...question,
            영상: null, // URL 생성 실패 시 null
            _originalFileName: fileName,
            _originalPath: originalPath
          };
        }
      }
      return question;
    });


    return processedQuestions;
  }

  /**
   * 파일 확장자로부터 Content-Type 추출
   * @param {string} fileName 
   * @returns {string}
   */
  getContentType(fileName) {
    const ext = fileName.split('.').pop().toLowerCase();
    const contentTypes = {
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'mov': 'video/quicktime',
      'avi': 'video/x-msvideo',
      'mkv': 'video/x-matroska'
    };
    return contentTypes[ext] || 'video/mp4';
  }

  /**
   * 캐시 초기화
   */
  clearCache() {
    this.cache.clear();

  }

  /**
   * TTS 스크립트 데이터의 오디오 필드를 Signed URL로 변환
   * @param {Array<Object>} episodes - TTS 에피소드 배열
   * @returns {Promise<Array<Object>>} URL이 변환된 에피소드 배열
   */
  async processPodcastEpisodes(episodes) {
    if (!Array.isArray(episodes)) {
      return episodes;
    }

    // section_order가 0인 코스 제목은 제외 (오디오 없음)
    const audioEpisodes = episodes.filter(episode => 
      episode.section_order && episode.section_order > 0
    );
    
    if (audioEpisodes.length === 0) {

      return episodes;
    }



    // section_title을 기반으로 GCS 파일 경로 생성
    const fileNames = audioEpisodes.map(episode => {
      // course_name을 폴더명으로, section_order를 파일명으로 사용
      const folderName = episode.course_name || 'default';
      const fileName = episode.section_order;
      return `podcast/${folderName}/${fileName}.mp3`;
    });
    
    const uniqueFileNames = [...new Set(fileNames)];


    // 배치로 Signed URL 생성
    const urlMap = await this.getBatchSignedUrls(uniqueFileNames);

    // 에피소드 데이터에 Signed URL 적용
    const processedEpisodes = episodes.map(episode => {
      if (episode.section_order && episode.section_order > 0) {
        // 새로운 폴더/파일 구조에 맞는 파일명 패턴 생성
        const folderName = episode.course_name || 'default';
        const fileName = episode.section_order;
        
        // 여러 가지 파일명 패턴 시도
        const possibleFileNames = [
          `podcast/${folderName}/${fileName}.mp3`, // 새로운 구조: podcast/폴더명/순서.mp3
        ];
        

        
        let audioFileName = null;
        let signedUrl = null;
        
        // 가능한 파일명들을 순차적으로 확인
        for (const fileName of possibleFileNames) {

          if (urlMap[fileName]) {
            audioFileName = fileName;
            signedUrl = urlMap[fileName];

            break;
          }
        }
        
        // 실제 GCS에 있는 파일명과의 차이를 확인하기 위한 로그
        if (!signedUrl) {
          console.warn(`❌ 모든 매핑 실패: ${episode.section_title}`);


          
          // 유사한 파일명 찾기 시도
          const availableFiles = Object.keys(urlMap).filter(key => key.startsWith('podcast/'));
          const similarFile = availableFiles.find(file => {
            const baseName = file.replace('podcast/', '').replace('_part1.mp3', '');
            return episode.section_title.includes(baseName.substring(0, 10)) || 
                   baseName.includes(episode.section_title.substring(0, 10));
          });
          
          if (similarFile) {

            audioFileName = similarFile;
            signedUrl = urlMap[similarFile];
          }
        }
        
        if (signedUrl) {
          return {
            ...episode,
            audio_url: signedUrl,
            _originalFileName: audioFileName,
            _hasAudio: true
          };
        } else {
          console.warn(`파일 ${audioFileName}의 Signed URL 생성 실패`);
          return {
            ...episode,
            audio_url: null,
            _originalFileName: audioFileName,
            _hasAudio: false
          };
        }
      }
      
      // section_order가 0인 경우 (코스 제목)
      return {
        ...episode,
        audio_url: null,
        _hasAudio: false
      };
    });


    return processedEpisodes;
  }

  /**
   * 캐시 상태 확인
   */
  getCacheStatus() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// 싱글톤 인스턴스 생성
const gcsService = new GCSService();

export default gcsService;